<template>
  <div v-if="show" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-fade-in">
    <!-- Contenedor Principal Modal -->
    <div class="bg-white dark:bg-slate-900 w-full max-w-7xl rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col max-h-[95vh]">

      <!-- Encabezado Estándar -->
      <div class="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/30">
        <div>
          <h2 class="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Radio class="w-5 h-5 text-emerald-500 animate-pulse" />
            Expediente de Pedido en Curso #{{ form.tracking_code || form.id }}
          </h2>
          <p class="text-xs text-slate-500">
            Modo Solo Lectura | Creado: {{ formatDateTime(form.created_at) }}
          </p>
        </div>
        <div class="flex items-center gap-3">
          <span
            class="px-3 py-1 rounded-full text-xs font-bold border"
            :class="form.status === 'ASSIGNED' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-purple-50 text-purple-700 border-purple-200'"
          >
            {{ form.status === 'ASSIGNED' ? '🔵 ASIGNADO' : '🟣 EN RUTA' }}
          </span>
          <button @click="$emit('close')" class="p-2 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-full transition-colors text-slate-500">
            <X class="w-5 h-5" />
          </button>
        </div>
      </div>

      <!-- Barra de Pestañas Horizontal -->
      <div class="flex overflow-x-auto bg-slate-50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800">
        <button v-for="tab in tabs" :key="tab.id" @click="activeTab = tab.id"
          :class="['px-6 py-3 text-xs font-bold uppercase tracking-wider whitespace-nowrap transition-all border-b-2',
          activeTab === tab.id ? 'border-[#F36421] text-[#F36421] bg-white dark:bg-slate-900' : 'border-transparent text-slate-500 hover:text-slate-700']">
          {{ tab.name }}
        </button>
      </div>

      <!-- Cuerpo del Formulario (5 Cols Datos Readonly Izquierda | 7 Cols Mapa Derecha) -->
      <div class="flex-1 overflow-y-auto p-6 custom-scrollbar">
        <form id="currentOrderForm" @submit.prevent novalidate>
          <div class="grid grid-cols-1 lg:grid-cols-12 gap-6">

            <!-- =============================================================== -->
            <!-- COLUMNA IZQUIERDA (5 COLS): CAMPOS BLOQUEADOS / READ-ONLY -->
            <!-- =============================================================== -->
            <div class="lg:col-span-5 space-y-4">

              <!-- PESTAÑA 1: ORIGEN & COMERCIO -->
              <div v-show="activeTab === 'pickup'" class="space-y-4 animate-fade-in">
                <div :class="sectionCardClasses" class="space-y-4">
                  <div class="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-3">
                    <h3 :class="[sectionTitleClasses, 'mb-0']">
                      <Store class="w-4 h-4 text-[#F36421]" />
                      Comercio & Origen (Pickup)
                    </h3>
                    <span class="text-[10px] font-bold px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400">
                      🟢 Origen
                    </span>
                  </div>

                  <div>
                    <label :class="labelClasses">Comercio / Cliente Solicitante</label>
                    <input :value="selectedClientName" type="text" :class="readonlyInputClasses" readonly />
                  </div>

                  <div v-if="selectedAddressAlias">
                    <label :class="labelClasses">Sucursal / Libreta (`customer_addresses`)</label>
                    <input :value="selectedAddressAlias" type="text" :class="readonlyInputClasses" readonly />
                  </div>

                  <div class="grid grid-cols-2 gap-3">
                    <div>
                      <label :class="labelClasses">Latitud Origen</label>
                      <input :value="form.pickup_lat" type="text" :class="readonlyInputClasses" readonly />
                    </div>
                    <div>
                      <label :class="labelClasses">Longitud Origen</label>
                      <input :value="form.pickup_lng" type="text" :class="readonlyInputClasses" readonly />
                    </div>
                  </div>

                  <div>
                    <label :class="labelClasses">Dirección Exacta de Recolección</label>
                    <textarea :value="form.pickup_address" rows="2" :class="readonlyInputClasses" readonly></textarea>
                  </div>
                </div>
              </div>

              <!-- PESTAÑA 2: DESTINO & RECEPTOR -->
              <div v-show="activeTab === 'dropoff'" class="space-y-4 animate-fade-in">
                <div :class="sectionCardClasses" class="space-y-4">
                  <div class="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-3">
                    <h3 :class="[sectionTitleClasses, 'mb-0']">
                      <MapPin class="w-4 h-4 text-red-500" />
                      Destino & Receptor (Dropoff)
                    </h3>
                    <span class="text-[10px] font-bold px-2.5 py-1 rounded-full bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                      🔴 Entrega
                    </span>
                  </div>

                  <div class="grid grid-cols-2 gap-3">
                    <div>
                      <label :class="labelClasses">Latitud Destino</label>
                      <input :value="form.dropoff_lat" type="text" :class="readonlyInputClasses" readonly />
                    </div>
                    <div>
                      <label :class="labelClasses">Longitud Destino</label>
                      <input :value="form.dropoff_lng" type="text" :class="readonlyInputClasses" readonly />
                    </div>
                  </div>

                  <div>
                    <label :class="labelClasses">Dirección / Señas de Entrega</label>
                    <textarea :value="form.dropoff_address" rows="2" :class="readonlyInputClasses" readonly></textarea>
                  </div>

                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label :class="labelClasses">Nombre Quien Recibe</label>
                      <input :value="form.receiver_name || 'No especificado'" type="text" :class="readonlyInputClasses" readonly />
                    </div>
                    <div>
                      <label :class="labelClasses">WhatsApp Receptor</label>
                      <input :value="form.receiver_phone || 'Sin número'" type="text" :class="readonlyInputClasses" readonly />
                    </div>
                  </div>
                </div>
              </div>

              <!-- PESTAÑA 3: MOTORIZADO, FINANZAS & ESTADO -->
              <div v-show="activeTab === 'pricing'" class="space-y-4 animate-fade-in">
                <!-- Seccion de Reasignacion de Motorizado -->
                <div :class="sectionCardClasses" class="space-y-3">
                  <div class="flex justify-between items-center border-b border-slate-100 dark:border-slate-800 pb-2">
                    <h3 :class="[sectionTitleClasses, 'mb-0']">
                      <Truck class="w-4 h-4 text-[#F36421]" />
                      Motorizado Asignado
                    </h3>
                  </div>

                  <div>
                    <label :class="labelClasses">* Conductor en Calle (Puedes Reasignar)</label>
                    <select v-model="form.driver_id" @change="onDriverChange" :class="inputClasses">
                      <option value="" disabled>Selecciona un motorizado...</option>
                      <option v-for="driver in driversList" :key="driver.id" :value="driver.id">
                        🛵 {{ driver.first_name }} {{ driver.last_name }} — Placa: {{ driver.license_plate || 'N/D' }}
                      </option>
                    </select>
                  </div>
                </div>

                <!-- Detalle Financiero -->
                <div :class="sectionCardClasses" class="space-y-3 font-mono text-xs">
                  <div class="flex justify-between items-center text-slate-500 uppercase border-b border-slate-100 dark:border-slate-800 pb-2">
                    <span>Método de Pago</span>
                    <span class="font-bold text-slate-800 dark:text-slate-200">{{ getPaymentMethodLabel(form.payment_method) }}</span>
                  </div>

                  <div class="flex justify-between items-center text-slate-600 dark:text-slate-400">
                    <span>Distancia Recorrida:</span>
                    <span class="font-bold text-slate-900 dark:text-white">{{ form.distance_km }} km ({{ form.duration_mins }} min)</span>
                  </div>

                  <div class="flex justify-between items-center text-slate-600 dark:text-slate-400">
                    <span>Tarifa Base:</span>
                    <span class="font-bold">C$ {{ formatMoney(form.base_fare) }}</span>
                  </div>

                  <div class="flex justify-between items-center text-slate-600 dark:text-slate-400">
                    <span>Cargo por KM Extra:</span>
                    <span class="font-bold">C$ {{ formatMoney(form.km_fee) }}</span>
                  </div>

                  <div class="border-t border-slate-200 dark:border-slate-700 pt-2 flex justify-between items-center text-sm font-bold text-slate-900 dark:text-white">
                    <span>FLETE TOTAL:</span>
                    <span class="text-base text-[#F36421]">C$ {{ formatMoney(form.total_delivery_fee) }}</span>
                  </div>

                  <div v-if="form.product_value_cod > 0" class="p-2.5 bg-amber-50 dark:bg-amber-950/20 rounded-lg border border-amber-200 dark:border-amber-800 flex justify-between items-center">
                    <span class="font-sans font-bold text-amber-800 dark:text-amber-300">💵 Cobro Efectivo (COD):</span>
                    <span class="font-bold text-amber-900 dark:text-amber-200">C$ {{ formatMoney(form.product_value_cod) }}</span>
                  </div>

                  <!-- Reparto 75/25 -->
                  <div class="grid grid-cols-3 gap-1.5 pt-2 border-t border-slate-200 dark:border-slate-700 text-[10px] text-center">
                    <div class="bg-blue-50 dark:bg-blue-950/30 p-1.5 rounded-lg border border-blue-200 dark:border-blue-800">
                      <div class="text-slate-500 font-sans">Chofer (75%)</div>
                      <div class="font-bold text-blue-600">C$ {{ formatMoney(form.driver_commission_fee) }}</div>
                    </div>
                    <div class="bg-orange-50 dark:bg-orange-950/30 p-1.5 rounded-lg border border-[#F36421]/30">
                      <div class="text-slate-500 font-sans">LLEVO (25%)</div>
                      <div class="font-bold text-[#F36421]">C$ {{ formatMoney(form.llevo_net_profit) }}</div>
                    </div>
                    <div class="bg-emerald-50 dark:bg-emerald-950/30 p-1.5 rounded-lg border border-emerald-200 dark:border-emerald-800">
                      <div class="text-slate-500 font-sans">Agencia USD</div>
                      <div class="font-bold text-emerald-600">${{ formatMoney(form.agency_usd) }}</div>
                    </div>
                  </div>
                </div>
              </div>

            </div>

            <!-- =============================================================== -->
            <!-- COLUMNA DERECHA (7 COLS): MAPA RUTA CREADA EN BD (WAYPOINTS) -->
            <!-- =============================================================== -->
            <div class="lg:col-span-7 flex flex-col">
              <div :class="[sectionCardClasses, 'p-0 overflow-hidden flex-1 min-h-[460px] flex flex-col relative']">
                <div class="px-4 py-3 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
                  <span class="text-xs font-bold text-slate-700 dark:text-slate-200 flex items-center gap-1.5">
                    <Navigation class="w-4 h-4 text-blue-600" />
                    Ruta Almacenada en Base de Datos (`route_geometry`)
                  </span>
                  <span class="text-xs font-mono font-bold text-emerald-600">
                    ✔ Distancia: {{ form.distance_km }} km
                  </span>
                </div>

                <!-- Contenedor del Mapa Leaflet -->
                <div id="current-order-map" class="w-full flex-1 min-h-[420px] z-10"></div>
              </div>
            </div>

          </div>
        </form>
      </div>

      <!-- Pie del Modal: Acciones Exclusivas de Pedido en Curso (Reasignar / Cancelar) -->
      <div class="px-6 py-4 bg-white dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800 flex justify-between items-center z-20">
        <button
          type="button"
          @click="openCancelDialog"
          class="px-4 py-2.5 text-xs font-bold text-red-600 bg-red-50 hover:bg-red-100 rounded-xl transition-colors flex items-center gap-1.5"
        >
          <XCircle class="w-4 h-4" /> Cancelar Pedido
        </button>

        <div class="flex gap-3 w-full sm:w-auto justify-end">
          <button @click="$emit('close')" type="button" :class="btnSecondary">
            Cerrar Expediente
          </button>
          <button
            v-if="isDriverChanged"
            @click="saveDriverReassignment"
            type="button"
            :disabled="isSaving"
            :class="btnPrimary"
          >
            <Loader2 v-if="isSaving" class="w-4 h-4 animate-spin" />
            <Save v-else class="w-4 h-4" />
            Guardar Cambio de Motorizado
          </button>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, nextTick } from 'vue';
import {
  Package, Store, MapPin, Navigation, Loader2, X,
  Truck, Radio, XCircle, Save
} from 'lucide-vue-next';
import { useToast } from '@/composables/useToast';
import apiClient from '@/api/client';
import Swal from 'sweetalert2';

// LEAFLET
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const props = defineProps({ show: Boolean, orderToView: Object });
const emit = defineEmits(['close', 'saved']);

const { showSuccess, showError } = useToast();
const isSaving = ref(false);
const isDriverChanged = ref(false);

const activeTab = ref('pickup');
const tabs = [
  { id: 'pickup', name: '1. Origen (Pickup)' },
  { id: 'dropoff', name: '2. Destino (Dropoff)' },
  { id: 'pricing', name: '3. Chofer & Finanzas' }
];

const inputClasses = "w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F36421]/20 focus:border-[#F36421] transition-all text-slate-700 dark:text-slate-200";
const readonlyInputClasses = "w-full px-3 py-2 bg-slate-100 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-600 dark:text-slate-300 font-medium cursor-not-allowed";
const labelClasses = "text-[10px] font-bold text-slate-500 uppercase mb-1 block";
const sectionCardClasses = "bg-slate-50/50 dark:bg-slate-800/30 p-5 rounded-xl border border-slate-100 dark:border-slate-800";
const sectionTitleClasses = "text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2 mb-4 uppercase tracking-tight";
const btnPrimary = "px-6 py-2.5 text-sm font-bold text-white bg-[#F36421] rounded-xl hover:bg-[#d95214] shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-70 w-full sm:w-auto";
const btnSecondary = "px-4 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 transition-colors w-full sm:w-auto";

const clientsList = ref([]);
const driversList = ref([]);

const form = reactive({
  id: null,
  tracking_code: '',
  client_id: '',
  driver_id: '',
  service_type: '',
  payment_method: 'CASH_ON_DELIVERY',
  pickup_lat: 12.148421,
  pickup_lng: -86.244974,
  pickup_address: '',
  dropoff_lat: 12.093710,
  dropoff_lng: -86.306877,
  dropoff_address: '',
  receiver_name: '',
  receiver_phone: '',
  distance_km: 0.00,
  duration_mins: 0,
  route_geometry: '',
  product_value_cod: 0.00,
  base_fare: 0.00,
  km_fee: 0.00,
  extra_charges: 0.00,
  total_delivery_fee: 0.00,
  driver_commission_fee: 0.00,
  llevo_net_profit: 0.00,
  agency_usd: 0.00,
  status: 'ASSIGNED',
  created_at: null
});

let mapInstance = null;

const originIcon = L.divIcon({
  className: 'custom-pin-origin',
  html: `<div style="font-size: 28px; line-height: 1; transform: translate(-50%, -100%);">🟢</div>`,
  iconSize: [28, 28], iconAnchor: [14, 28]
});

const destinationIcon = L.divIcon({
  className: 'custom-pin-dest',
  html: `<div style="font-size: 28px; line-height: 1; transform: translate(-50%, -100%);">🔴</div>`,
  iconSize: [28, 28], iconAnchor: [14, 28]
});

const selectedClientName = computed(() => {
  const c = clientsList.value.find(i => i.id === form.client_id);
  return c ? (c.business_name || `${c.first_name} ${c.last_name}`) : 'Comercio Registrado';
});

const selectedAddressAlias = computed(() => {
  return form.pickup_address.includes('—') ? form.pickup_address.split('—')[0] : null;
});

// ============================================================================
// DIBUJAR MAPA LEAFLET UTILIZANDO `route_geometry` GUARDADO EN BD
// ============================================================================
const initMap = () => {
  nextTick(() => {
    const container = document.getElementById('current-order-map');
    if (!container) return;

    const latA = Number(form.pickup_lat) || 12.148421;
    const lngA = Number(form.pickup_lng) || -86.244974;
    const latB = Number(form.dropoff_lat) || 12.093710;
    const lngB = Number(form.dropoff_lng) || -86.306877;

    if (!mapInstance) {
      mapInstance = L.map('current-order-map').setView([latA, lngA], 13);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors', maxZoom: 19
      }).addTo(mapInstance);
    } else {
      mapInstance.eachLayer((layer) => {
        if (layer instanceof L.Marker || layer instanceof L.Polyline) mapInstance.removeLayer(layer);
      });
    }

    // Dibujar marcadores Origen y Destino
    L.marker([latA, lngA], { icon: originIcon }).addTo(mapInstance);
    L.marker([latB, lngB], { icon: destinationIcon }).addTo(mapInstance);

    // DIBUJAR RUTA DESDE BD (SIN VOLVER A LLAMAR OSRM)
    if (form.route_geometry) {
      try {
        const coords = JSON.parse(form.route_geometry);
        const latLngs = coords.map(c => [c[1], c[0]]);
        const polyline = L.polyline(latLngs, { color: '#0066FF', weight: 5, opacity: 0.85 }).addTo(mapInstance);
        mapInstance.fitBounds(polyline.getBounds(), { padding: [40, 40] });
      } catch (e) {
        console.error('Error parseando route_geometry:', e);
      }
    } else {
      // Fallback por si la orden antigua no tenía geometry
      const bounds = L.latLngBounds([[latA, lngA], [latB, lngB]]);
      mapInstance.fitBounds(bounds, { padding: [40, 40] });
    }

    setTimeout(() => mapInstance?.invalidateSize(), 200);
  });
};

// Cargar Catálogos Auxiliares
const loadClients = async () => {
  try {
    const res = await apiClient.get('/customers');
    clientsList.value = Array.isArray(res.data) ? res.data : (res.data.data || []);
  } catch (e) { console.error(e); }
};

const loadDrivers = async () => {
  try {
    const res = await apiClient.get('/drivers');
    const all = Array.isArray(res.data) ? res.data : (res.data.data || []);
    driversList.value = all.filter(d => d.status === 'ACTIVE' || !d.status);
  } catch (e) { console.error(e); }
};

const onDriverChange = () => {
  isDriverChanged.value = true;
};

const saveDriverReassignment = async () => {
  if (!form.driver_id) return;
  isSaving.value = true;
  try {
    const driver = driversList.value.find(d => d.id === form.driver_id);
    await apiClient.put(`/orders/${form.id}`, {
      ...form,
      driver_id: form.driver_id,
      driver_name: driver ? `${driver.first_name} ${driver.last_name}` : '',
      driver_plate: driver?.license_plate || '',
      status: 'ASSIGNED'
    });

    showSuccess('✔ Reasignación de motorizado guardada.');
    isDriverChanged.value = false;
    emit('saved');
    emit('close');
  } catch (e) {
    showError('No se pudo reasignar el conductor.');
  } finally {
    isSaving.value = false;
  }
};

const openCancelDialog = () => {
  Swal.fire({
    title: '¿Cancelar Pedido en Curso?',
    text: `Guía #${form.tracking_code || form.id}. Indica la razón de cancelación:`,
    icon: 'warning',
    input: 'select',
    inputOptions: {
      'CLIENTE_CANCELO': 'Cliente canceló el pedido',
      'COMERCIO_SIN_STOCK': 'Comercio sin producto',
      'AVERIA_MOTORIZADO': 'Avería o pinchazo de moto en ruta',
      'OTRO': 'Otro motivo de central'
    },
    inputPlaceholder: 'Selecciona motivo...',
    showCancelButton: true,
    confirmButtonColor: '#ef4444',
    confirmButtonText: 'Sí, cancelar orden',
    cancelButtonText: 'Volver',
    inputValidator: (v) => !v && '¡Debes elegir un motivo obligatoriamente!'
  }).then(async (res) => {
    if (res.isConfirmed) {
      try {
        await apiClient.put(`/orders/${form.id}`, {
          ...form,
          status: 'CANCELLED',
          cancellation_reason: res.value
        });
        showSuccess('Orden cancelada.');
        emit('saved');
        emit('close');
      } catch (e) {
        showError('No se pudo cancelar el pedido.');
      }
    }
  });
};

const formatMoney = (val) => Number(val || 0).toLocaleString('en-US', { minimumFractionDigits: 2 });
const formatDateTime = (iso) => iso ? new Date(iso).toLocaleDateString('es-NI', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '--';
const getPaymentMethodLabel = (m) => {
  const map = { 'CASH_ON_DELIVERY': '💵 Efectivo Entrega', 'PAID_TO_BUSINESS': '💳 Pagado al Negocio', 'DIRECT_SHIPPING_PAID': '🤝 Pago Directo' };
  return map[m] || '💵 Efectivo';
};

watch(() => props.show, async (isOpen) => {
  if (isOpen) {
    activeTab.value = 'pickup';
    isDriverChanged.value = false;
    await loadClients();
    await loadDrivers();

    if (props.orderToView) {
      Object.assign(form, props.orderToView);
    }
    initMap();
  }
});
</script>

<style scoped>
@keyframes fade-in { from { opacity: 0; transform: scale(0.98); } to { opacity: 1; transform: scale(1); } }
.animate-fade-in { animation: fade-in 0.2s ease-out forwards; }
.custom-scrollbar::-webkit-scrollbar { width: 4px; }
.custom-scrollbar::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 20px; }
</style>